import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Home as HomeIcon, Users, HeartHandshake } from 'lucide-react';
import { Link } from 'react-router-dom';
import Hero from '../components/Hero';
import Testimonials from '../components/Testimonials';
import Stats from '../components/Stats';

const Home = () => {
    return (
        <>
            <Helmet>
                <title>Community Plus Care Services | NDIS Provider SA & QLD</title>
                <meta name="description" content="Community Plus Care Services provides Supported Independent Living (SIL), Community Participation, and Personal Care for NDIS participants in SA and QLD." />
                <meta name="keywords" content="NDIS, SIL, Supported Independent Living, Disability Support, Adelaide, Brisbane, Community Care" />
            </Helmet>

            <main>
                <Hero />

                {/* What is NDIS Section */}
                <section className="py-20 bg-[#EFF6FF]">
                    <div className="container mx-auto px-4 flex flex-col md:flex-row items-center gap-12">
                        <div className="w-full md:w-1/2">
                            <img
                                src="/assets/young-adult-watching-tv-show-with-disabled-friend.jpg"
                                alt="What is NDIS"
                                className="rounded-3xl shadow-2xl w-full object-cover h-[400px]"
                            />
                        </div>
                        <div className="w-full md:w-1/2">
                            <h2 className="text-[#D99C3B] font-serif text-4xl font-bold mb-6">What is NDIS?</h2>
                            <p className="text-gray-600 mb-6 leading-relaxed">
                                The National Disability Insurance Scheme (NDIS) provides support to eligible people with intellectual, physical, sensory, cognitive and psychosocial disability. Early intervention supports can also be provided for eligible people with disability or children with developmental delay.
                            </p>
                            <p className="text-gray-600 leading-relaxed">
                                NDIS gives you more choice and control over how, when and where your supports are provided, and gives you certainty you will get the support you need.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Services Preview Section */}
                <section className="py-20 bg-white">
                    <div className="container mx-auto px-4">
                        <div className="text-center mb-16">
                            <h2 className="text-[#D99C3B] font-serif text-4xl md:text-5xl font-bold mb-4">Our Services</h2>
                            <div className="w-24 h-1 bg-[#008080] mx-auto rounded-full opacity-50"></div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                            {/* Card 1 */}
                            <div className="bg-white p-8 rounded-2xl shadow-lg border-t-4 border-[#008080] hover:shadow-2xl transition-all duration-300 flex flex-col items-center text-center group h-full">
                                <div className="w-20 h-20 bg-[#008080] rounded-full flex items-center justify-center mb-6 text-white group-hover:scale-110 transition-transform duration-300">
                                    <HomeIcon size={40} strokeWidth={1.5} />
                                </div>
                                <h3 className="text-xl font-bold text-[#008080] mb-4 font-serif tracking-wide">Supported Independent Living (SIL)</h3>
                                <p className="text-gray-500 leading-relaxed text-sm">
                                    Personalized in-home support to help you live as independently as possible while receiving the care you need 24/7.
                                </p>
                            </div>

                            {/* Card 2 */}
                            <div className="bg-white p-8 rounded-2xl shadow-lg border-t-4 border-[#008080] hover:shadow-2xl transition-all duration-300 flex flex-col items-center text-center group h-full">
                                <div className="w-20 h-20 bg-[#008080] rounded-full flex items-center justify-center mb-6 text-white group-hover:scale-110 transition-transform duration-300">
                                    <Users size={40} strokeWidth={1.5} />
                                </div>
                                <h3 className="text-xl font-bold text-[#008080] mb-4 font-serif tracking-wide">Community Participation</h3>
                                <p className="text-gray-500 leading-relaxed text-sm">
                                    Engage with your community through social events, skill-building workshops, and group activities tailored to your interests.
                                </p>
                            </div>

                            {/* Card 3 */}
                            <div className="bg-white p-8 rounded-2xl shadow-lg border-t-4 border-[#008080] hover:shadow-2xl transition-all duration-300 flex flex-col items-center text-center group h-full">
                                <div className="w-20 h-20 bg-[#008080] rounded-full flex items-center justify-center mb-6 text-white group-hover:scale-110 transition-transform duration-300">
                                    <HeartHandshake size={40} strokeWidth={1.5} />
                                </div>
                                <h3 className="text-xl font-bold text-[#008080] mb-4 font-serif tracking-wide">Daily Personal Activities</h3>
                                <p className="text-gray-500 leading-relaxed text-sm">
                                    Assistance with daily living tasks such as personal hygiene, meal preparation, and medication management to enhance your wellbeing.
                                </p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Stats Counter Section */}
                <Stats />

                {/* Empowering Lives Section */}
                <section className="py-20 bg-[#EFF6FF]">
                    <div className="container mx-auto px-4 flex flex-col-reverse md:flex-row items-center gap-12">
                        <div className="w-full md:w-1/2">
                            <h2 className="text-[#D99C3B] font-serif text-4xl font-bold mb-6">Empowering Lives</h2>
                            <p className="text-gray-600 mb-4 leading-relaxed text-lg">
                                We are engaging and collaborating with First Nations communities to provide culturally appropriate support. Our experience in Supported Independent Living (SIL) and Specialist Disability Accommodation (SDA) ensures that you have a safe and supportive environment to call home.
                            </p>
                            <p className="text-[#008080] font-medium mb-8 leading-relaxed italic">
                                We are proud to work alongside Aboriginal and Torres Strait Islander communities, supporting health, wellbeing, and connection to culture.
                            </p>
                            <Link to="/register" className="inline-block bg-[#008080] text-white font-bold py-3 px-8 rounded-full hover:bg-teal-700 transition-colors shadow-md">
                                JOIN OUR COMMUNITY
                            </Link>
                        </div>
                        <div className="w-full md:w-1/2">
                            <img
                                src="/assets/people-helping-old-neighbor.jpg"
                                alt="Empowering Lives"
                                className="rounded-3xl shadow-2xl w-full object-cover h-[400px]"
                            />
                        </div>
                    </div>
                </section>

                <Testimonials />
            </main>
        </>
    );
};

export default Home;
